// ============================================
// NOTIFICATION CONTEXT - Push & In-App Notifications
// ============================================

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { pushNotificationService } from '../services/pushNotification.service';
import { notificationService, Notification } from '../services/notificationService';
import { useAuth } from './AuthContext';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  // Actions
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  // Push notification toast (for foreground notifications)
  toastNotification: { title: string; body: string } | null;
  dismissToast: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toastNotification, setToastNotification] = useState<{ title: string; body: string } | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pushInitializedRef = useRef(false);

  // Initialize push notifications when user logs in
  useEffect(() => {
    if (!user) {
      // User logged out - cleanup
      pushInitializedRef.current = false;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      return;
    }

    // Small delay to ensure token is in localStorage after login
    const initTimer = setTimeout(async () => {
      // Initialize Capacitor push notifications
      console.log('[PUSH] User detected, initializing push notifications...');
      await pushNotificationService.initialize();
      pushInitializedRef.current = true;

      // Set up foreground notification handler
      pushNotificationService.onNotification((notification) => {
        // Show toast for foreground notifications
        setToastNotification({
          title: notification.title || 'Notificare nouă',
          body: notification.body || ''
        });

        // Auto-dismiss after 5 seconds
        if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
        toastTimeoutRef.current = setTimeout(() => {
          setToastNotification(null);
        }, 5000);

        // Refresh unread count
        refreshUnreadCount();
      });

      // Initial fetch
      refreshUnreadCount();

      // Poll for unread count every 30 seconds
      pollIntervalRef.current = setInterval(() => {
        refreshUnreadCount();
      }, 30000);
    }, 500);

    return () => {
      clearTimeout(initTimer);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      pushNotificationService.cleanup();
    };
  }, [user?.id]);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      // Silently fail - don't break the app for notification errors
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const result = await notificationService.getNotifications(1, 50);
      setNotifications(result.notifications);
      setUnreadCount(result.unreadCount);
    } catch (error) {
      console.error('[NOTIFICATIONS] Error fetching:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('[NOTIFICATIONS] Error marking as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('[NOTIFICATIONS] Error marking all as read:', error);
    }
  }, []);

  const dismissToast = useCallback(() => {
    setToastNotification(null);
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        refreshUnreadCount,
        toastNotification,
        dismissToast
      }}
    >
      {children}

      {/* Floating Toast for foreground notifications */}
      {toastNotification && (
        <div
          className="fixed top-4 right-4 z-[9999] max-w-sm w-full bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 animate-slide-in-right cursor-pointer"
          onClick={dismissToast}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {toastNotification.title}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                {toastNotification.body}
              </p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); dismissToast(); }}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
